import { getDatabase, onValue, ref } from 'firebase/database'
import { useEffect, memo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
    SchoolInformation,
    SchoolUser,
} from '../../../../firebase/types-school'
import {
    setSchool,
    setSchool_v2,
    setSchoolUser,
    clearSchoolData,
} from '../../../../redux/schoolSlice'
import { RootState } from '../../../../redux/store'
import {
    userSchoolsCountSelector,
    selectUserUid,
} from '../../../../redux/userSlice'
import { logger } from '../../../../helpers/logger'

const SchoolListeners = ({ schoolUid }: { schoolUid: string }) => {
    const authed = useSelector((state: RootState) => !!state.user.auth.status)
    const schoolsCount = useSelector(userSchoolsCountSelector)

    const userId = useSelector(selectUserUid)!
    const dispatch = useDispatch()

    const shouldRedirectToOnboarding =
        !authed || schoolsCount === null || schoolsCount === 0

    // Attach
    useEffect(() => {
        if (!userId) return
        const schoolRef = ref(getDatabase(), `schoolInformation/${schoolUid}`)
        const unsub = onValue(schoolRef, (snapshot) => {
            if (snapshot.exists()) {
                const schoolData = snapshot.val()
                let schoolFields: SchoolInformation = {
                    id: schoolUid,
                    ...schoolData,
                    ...(schoolData.account
                        ? { account: schoolData.account }
                        : { account: { state: 'pendingApproval' } }),
                    ...(schoolData.businessManager
                        ? { businessManager: schoolData.businessManager }
                        : { businessManager: { state: 'incomplete' } }),

                    logo: schoolData.logo || { state: 'incomplete' },
                    details: {
                        ...schoolData.details,
                        activeAutoReplacements: {
                            primarySupportStaff:
                                !!schoolData.details.activeAutoReplacements
                                    ?.primarySupportStaff,
                            primaryTeachers:
                                !!schoolData.details.activeAutoReplacements
                                    ?.primaryTeachers,
                            secondarySupportStaff:
                                !!schoolData.details.activeAutoReplacements
                                    ?.secondarySupportStaff,
                            secondaryTeachers:
                                !!schoolData.details.activeAutoReplacements
                                    ?.secondaryTeachers,
                        },
                    },
                    location: schoolData.location,
                    parking: schoolData.parking,
                    times: schoolData.times,
                    primaryTimes: schoolData.primaryTimes,
                    secondaryTimes: schoolData.secondaryTimes,
                    policies: schoolData.policies,
                }

                dispatch(setSchool(schoolFields))
                dispatch(setSchool_v2(schoolFields))
            } else {
                dispatch(setSchool_v2(null))
            }
        })

        const schoolUserNode = ref(
            getDatabase(),
            `schoolUsers/${schoolUid}/${userId}`
        )
        const schoolUserUnsub = onValue(schoolUserNode, (snapshot) => {
            if (snapshot.exists()) {
                const schoolUserData = snapshot.val() as SchoolUser
                dispatch(setSchoolUser(schoolUserData))
            } else {
                logger.debug(`School user does not exist!`)
            }
        })

        return () => {
            unsub()
            schoolUserUnsub()
            dispatch(clearSchoolData())
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schoolUid, userId, shouldRedirectToOnboarding])

    return null
}
export default memo(SchoolListeners)
